import { safeSessionStorage } from "@/lib/storage/safe-storage";
import { withReturnContext } from "./return-context";

export const RECORD_SEQUENCE_STORAGE_KEY = "repackify:record-sequences";

const INTERNAL_ORIGIN = "https://repackify.local";
const MAX_REMEMBERED_SEQUENCES = 8;
const MAX_REMEMBERED_HREFS = 500;

type RecordSequence = {
  objectId: string;
  sourcePath: string;
  hrefs: string[];
  savedAt: number;
};

export type RecordNeighbors = {
  previousHref: string | null;
  nextHref: string | null;
  position: number;
  total: number;
};

function pathnameOf(href: string): string | null {
  try {
    const url = new URL(href, INTERNAL_ORIGIN);
    return url.origin === INTERNAL_ORIGIN ? url.pathname : null;
  } catch {
    return null;
  }
}

function parentPathOf(pathname: string): string {
  return pathname.slice(0, pathname.lastIndexOf("/") + 1);
}

function isRecordSequence(value: unknown): value is RecordSequence {
  if (typeof value !== "object" || value === null) return false;
  const candidate: Partial<Record<keyof RecordSequence, unknown>> = value;
  return (
    typeof candidate.objectId === "string" &&
    typeof candidate.sourcePath === "string" &&
    typeof candidate.savedAt === "number" &&
    Array.isArray(candidate.hrefs) &&
    candidate.hrefs.every((href) => typeof href === "string")
  );
}

export function parseRecordSequences(raw: string | null): RecordSequence[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isRecordSequence) : [];
  } catch {
    return [];
  }
}

export function withRememberedSequence(
  raw: string | null,
  sequence: RecordSequence,
): string {
  const others = parseRecordSequences(raw).filter(
    (existing) =>
      existing.objectId !== sequence.objectId ||
      existing.sourcePath !== sequence.sourcePath,
  );
  const remembered = {
    ...sequence,
    hrefs: sequence.hrefs.slice(0, MAX_REMEMBERED_HREFS),
  };
  return JSON.stringify(
    [remembered, ...others].slice(0, MAX_REMEMBERED_SEQUENCES),
  );
}

function siblingHrefs(sequence: RecordSequence, parentPath: string) {
  return sequence.hrefs.filter((href) => {
    const pathname = pathnameOf(href);
    return pathname !== null && parentPathOf(pathname) === parentPath;
  });
}

export function findRecordNeighbors(
  sequences: RecordSequence[],
  currentPath: string,
  returnHref: string,
): RecordNeighbors | null {
  const parentPath = parentPathOf(currentPath);
  const returnPath = pathnameOf(returnHref);
  const match = sequences
    .map((sequence) => ({
      sequence,
      hrefs: siblingHrefs(sequence, parentPath),
    }))
    .map((entry) => ({
      ...entry,
      index: entry.hrefs.findIndex((href) => pathnameOf(href) === currentPath),
    }))
    .filter((entry) => entry.index !== -1)
    .sort(
      (left, right) =>
        Number(right.sequence.sourcePath === returnPath) -
          Number(left.sequence.sourcePath === returnPath) ||
        right.sequence.savedAt - left.sequence.savedAt,
    )[0];
  if (!match || match.hrefs.length < 2) return null;

  const neighborHref = (offset: number) => {
    const href = match.hrefs[match.index + offset];
    return href === undefined ? null : withReturnContext(href, returnHref);
  };
  return {
    previousHref: neighborHref(-1),
    nextHref: neighborHref(1),
    position: match.index + 1,
    total: match.hrefs.length,
  };
}

export function rememberRecordSequence(
  objectId: string,
  sourcePath: string,
  hrefs: string[],
) {
  const storage = safeSessionStorage();
  storage.setItem(
    RECORD_SEQUENCE_STORAGE_KEY,
    withRememberedSequence(storage.getItem(RECORD_SEQUENCE_STORAGE_KEY), {
      objectId,
      sourcePath,
      hrefs,
      savedAt: Date.now(),
    }),
  );
}

export function readRememberedSequences(): string | null {
  return safeSessionStorage().getItem(RECORD_SEQUENCE_STORAGE_KEY);
}
